// Autor: YJ
// Date: 2025.08
// Description: study tool for Security plus exam

import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import FlashcardSetSelector from './components/FlashcardSetSelector';
import Flashcard from './components/Flashcard';
import Loading from './components/Loading';
import { fetchFlashcardSet } from './services/flashcardApi';

function App() {
  const [selectedSet, setSelectedSet] = useState(null);
  const [flashcards, setFlashcards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const [flaggedCards, setFlaggedCards] = useState([]); // [{ setId, index }]
  const [reviewMode, setReviewMode] = useState(false);

  // Theme colors
  const theme = {
    light: {
      background: '#F7EEE5',
      cardBackground: 'white',
      primary: '#28341C',
      secondary: '#364327',
      accent: '#4D5C3C',
      optionBackground: '#F7EEE5',
      text: '#28341C',
      textLight: '#F7EEE5'
    },
    dark: {
      background: '#1a1f15',
      cardBackground: '#28341C',
      primary: '#F7EEE5',
      secondary: '#4D5C3C',
      accent: '#364327',
      optionBackground: '#364327',
      text: '#F7EEE5',
      textLight: '#28341C'
    }
  };

  const currentTheme = isDarkTheme ? theme.dark : theme.light;

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  React.useEffect(() => {
    document.body.style.backgroundColor = currentTheme.background;
    document.body.style.color = currentTheme.text;
  }, [currentTheme]);

  const loadFlashcardSet = async (set) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFlashcardSet(set.id);
      setFlashcards(data);
      setSelectedSet(set);
      setCurrentCardIndex(0);
    } catch (err) {
      setError(`Error loading flashcard set: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAnswer = useCallback(() => {
    setShowAnswer(true);
  }, []);

  const handleSetSelect = (set) => {
    loadFlashcardSet(set);
  };

  const resetToSetSelection = () => {
    setSelectedSet(null);
    setFlashcards([]);
    setCurrentCardIndex(0);
    setError(null);
    setShowAnswer(false);
  };

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('pmflashcards_flagged');
      if (stored) {
        setFlaggedCards(JSON.parse(stored));
      }
    } catch (err) {
      console.warn('Failed to read flagged cards from localStorage', err);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('pmflashcards_flagged', JSON.stringify(flaggedCards));
  }, [flaggedCards]);

  const selectedSetFlaggedIndexes = selectedSet
    ? flaggedCards.filter((f) => f.setId === selectedSet.id).map((f) => f.index).sort((a, b) => a - b)
    : [];

  const visibleFlashcards = reviewMode
    ? selectedSetFlaggedIndexes.map((i) => flashcards[i]).filter(Boolean)
    : flashcards;

  const flaggedCount = selectedSetFlaggedIndexes.length;

  const handleNext = useCallback(() => {
    if (currentCardIndex < visibleFlashcards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
      setShowAnswer(false);
    }
  }, [currentCardIndex, visibleFlashcards.length]);

  const handlePrevious = useCallback(() => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
      setShowAnswer(false);
    }
  }, [currentCardIndex]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedSet || visibleFlashcards.length === 0 || loading) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (currentCardIndex > 0) {
            handlePrevious();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentCardIndex < visibleFlashcards.length - 1) {
            handleNext();
          }
          break;
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          setShowAnswer(true);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedSet, visibleFlashcards, currentCardIndex, loading, handleNext, handlePrevious]);

  useEffect(() => {
    setCurrentCardIndex(0);
  }, [reviewMode, selectedSet?.id]);

  useEffect(() => {
    if (currentCardIndex >= visibleFlashcards.length && visibleFlashcards.length > 0) {
      setCurrentCardIndex(visibleFlashcards.length - 1);
    }
  }, [currentCardIndex, visibleFlashcards]);

  const globalIndexForCurrent = () => {
    if (!selectedSet || visibleFlashcards.length === 0) return null;
    if (reviewMode) {
      return selectedSetFlaggedIndexes[currentCardIndex];
    }
    return currentCardIndex;
  };

  const handleMarkWrong = () => {
    if (!selectedSet || flashcards.length === 0) return;
    const idx = globalIndexForCurrent();
    if (idx === null || flaggedCards.some((f) => f.setId === selectedSet.id && f.index === idx)) return;
    setFlaggedCards((prev) => [...prev, { setId: selectedSet.id, index: idx }]);
  };

  const handleUnflag = () => {
    if (!selectedSet || flashcards.length === 0) return;
    const idx = globalIndexForCurrent();
    if (idx === null) return;
    setFlaggedCards((prev) => prev.filter((f) => !(f.setId === selectedSet.id && f.index === idx)));
  };

  return (
    <div className="App" style={{ 
      padding: '20px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <header style={{ textAlign: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}></div>
          <h1 style={{ 
            color: currentTheme.primary,
            fontSize: '2.5rem',
            marginBottom: '10px',
            fontWeight: 'bold',
            flex: 2,
            textAlign: 'center'
          }}>LOCK THE F in YJ</h1>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={toggleTheme}
              style={{
                padding: '10px',
                backgroundColor: currentTheme.secondary,
                color: currentTheme.textLight,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = currentTheme.accent}
              onMouseOut={(e) => e.target.style.backgroundColor = currentTheme.secondary}
              title={isDarkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
            >
              {isDarkTheme ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
        {selectedSet && (
          <button
            className="btn btn-secondary"
            onClick={resetToSetSelection}
          >
            ← Back to Set Selection
          </button>
        )}
      </header>

      {error && (
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {error}
        </div>
      )}

      {loading && <Loading message="Loading flashcards..." theme={currentTheme} />}

      {!selectedSet && !loading && (
        <FlashcardSetSelector 
          onSetSelect={handleSetSelect}
          selectedSet={selectedSet}
          theme={currentTheme}
        />
      )}

      {selectedSet && flashcards.length > 0 && !loading && (
        <div>
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: currentTheme.primary,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'background-color 0.3s ease'
          }}>
            <h2 style={{ 
              color: currentTheme.textLight,
              margin: '0 0 10px 0',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>{selectedSet.name}</h2>
            <p style={{ 
              color: currentTheme.textLight, 
              fontSize: '16px', 
              margin: '0 0 15px 0',
              opacity: '0.9'
            }}>
              {flashcards.length} cards total
            </p>
            <p style={{ 
              color: currentTheme.textLight, 
              fontSize: '14px', 
              margin: '0',
              opacity: '0.8'
            }}>
              Use ← → arrow keys to navigate, spacebar to reveal answer
            </p>
          </div>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                setReviewMode(false);
                setCurrentCardIndex(0);
              }}
              disabled={!reviewMode}
            >
              Study full set
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setReviewMode(true);
                setCurrentCardIndex(0);
              }}
              disabled={flaggedCount === 0}
              style={{ marginLeft: '8px' }}
            >
              Review flagged ({flaggedCount})
            </button>
          </div>

          {selectedSet && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '12px' }}>
              <button className="btn btn-danger" onClick={handleMarkWrong}>
                Mark Wrong
              </button>
              <button className="btn btn-secondary" onClick={handleUnflag} disabled={flaggedCount === 0}>
                Unflag
              </button>
            </div>
          )}

          {selectedSet && flashcards.length > 0 && !loading && (
            <div>
              {reviewMode ? (
                <div>
                  <Flashcard
                    flashcard={visibleFlashcards[currentCardIndex]}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    hasNext={currentCardIndex < visibleFlashcards.length - 1}
                    hasPrevious={currentCardIndex > 0}
                    currentIndex={currentCardIndex}
                    totalCards={visibleFlashcards.length}
                    showAnswer={showAnswer}
                    onShowAnswer={handleShowAnswer}
                    theme={currentTheme}
                  />
                </div>
              ) : (
                <div>
                  <Flashcard
                    flashcard={flashcards[currentCardIndex]}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    hasNext={currentCardIndex < flashcards.length - 1}
                    hasPrevious={currentCardIndex > 0}
                    currentIndex={currentCardIndex}
                    totalCards={flashcards.length}
                    showAnswer={showAnswer}
                    onShowAnswer={handleShowAnswer}
                    theme={currentTheme}
                  />
                </div>
              )}

              {selectedSet && reviewMode && !loading && visibleFlashcards.length === 0 && (
                <p style={{ textAlign: 'center' }}>No flagged items yet for this set.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
